import { type FormEvent, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import {
  type Certificate,
  type CurrentUser,
  type Destination,
  api,
} from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Cable,
  KeyRound,
  MoreHorizontal,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Search,
  Server,
  Trash2,
} from "lucide-react"

type Props = {
  currentUser: CurrentUser
  destinations: Destination[]
  onDestinationsChange: (destinations: Destination[]) => void
  onMachineChange: (machineName: string) => void
}

type DestinationFormState = {
  machine_name: string
  ssh_host: string
  ssh_user: string
  openvpn_mode: string
  openvpn_script_path: string
  remote_file_template: string
  certificate_id: string
  ssh_key_path: string
}

const EMPTY_FORM: DestinationFormState = {
  machine_name: "",
  ssh_host: "",
  ssh_user: "ec2-user",
  openvpn_mode: "modern_cli",
  openvpn_script_path: "/root/openvpn-install.sh",
  remote_file_template: "~/{client_name}.ovpn",
  certificate_id: "none",
  ssh_key_path: "",
}

export function DestinationsPage({
  currentUser,
  destinations,
  onDestinationsChange,
  onMachineChange,
}: Props) {
  const [loading, setLoading] = useState(destinations.length === 0)
  const [search, setSearch] = useState("")
  const [certificates, setCertificates] = useState<Certificate[]>([])
  const [sheetOpen, setSheetOpen] = useState(false)
  const [editing, setEditing] = useState<Destination | null>(null)
  const [form, setForm] = useState<DestinationFormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Destination | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [testingName, setTestingName] = useState<string | null>(null)
  const [togglingName, setTogglingName] = useState<string | null>(null)

  const isAdmin = currentUser.permissions.includes("admin")

  useEffect(() => {
    if (!isAdmin) return
    Promise.all([api.getDestinations(true), api.getCertificates()])
      .then(([destRes, certRes]) => {
        onDestinationsChange(destRes.destinations)
        setCertificates(certRes.certificates)
      })
      .catch(() => toast.error("Erro ao carregar ambientes."))
      .finally(() => setLoading(false))
  }, [isAdmin])

  const certificateById = useMemo(
    () => new Map(certificates.map((cert) => [cert.id, cert])),
    [certificates]
  )

  const filteredDestinations = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return destinations
    return destinations.filter(
      (d) =>
        d.machine_name.toLowerCase().includes(q) ||
        d.ssh_host.toLowerCase().includes(q) ||
        d.ssh_user.toLowerCase().includes(q)
    )
  }, [destinations, search])

  const openNew = () => {
    const defaultCert = certificates[0]?.id
    setEditing(null)
    setForm({
      ...EMPTY_FORM,
      certificate_id: defaultCert ? String(defaultCert) : "none",
    })
    setSheetOpen(true)
  }

  const openEdit = (destination: Destination) => {
    setEditing(destination)
    setForm({
      machine_name: destination.machine_name,
      ssh_host: destination.ssh_host,
      ssh_user: destination.ssh_user,
      openvpn_mode: destination.openvpn_mode,
      openvpn_script_path: destination.openvpn_script_path,
      remote_file_template: destination.remote_file_template,
      certificate_id:
        destination.certificate_id == null
          ? "none"
          : String(destination.certificate_id),
      ssh_key_path: destination.ssh_key_path,
    })
    setSheetOpen(true)
  }

  const updateForm = (key: keyof DestinationFormState, value: string) => {
    setForm((current) => ({ ...current, [key]: value }))
  }

  const selectedCertificateId =
    form.certificate_id === "none" ? null : Number(form.certificate_id)

  const submitDisabled =
    saving ||
    !form.machine_name.trim() ||
    !form.ssh_host.trim() ||
    !form.ssh_user.trim() ||
    !form.openvpn_script_path.trim() ||
    !form.remote_file_template.trim() ||
    selectedCertificateId == null

  const buildPayload = () => ({
    machine_name: form.machine_name.trim(),
    ssh_host: form.ssh_host.trim(),
    ssh_user: form.ssh_user.trim(),
    openvpn_mode: form.openvpn_mode,
    openvpn_script_path: form.openvpn_script_path.trim(),
    remote_file_template: form.remote_file_template.trim(),
    certificate_id: selectedCertificateId,
    ssh_key_path: form.ssh_key_path.trim(),
  })

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitDisabled) return
    setSaving(true)
    try {
      const payload = buildPayload()
      const res = editing
        ? await api.updateDestination(editing.machine_name, payload)
        : await api.createDestination(payload)
      onDestinationsChange(res.destinations)
      onMachineChange(payload.machine_name)
      toast.success(res.log)
      setSheetOpen(false)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Erro ao salvar ambiente."
      )
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      const res = await api.deleteDestination(deleteTarget.machine_name)
      onDestinationsChange(res.destinations)
      onMachineChange(res.destinations[0]?.machine_name ?? "")
      toast.success(res.log)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Erro ao remover ambiente."
      )
    } finally {
      setDeleting(false)
      setDeleteTarget(null)
    }
  }

  const handleTest = async (destination: Destination) => {
    setTestingName(destination.machine_name)
    try {
      const res = await api.connect(destination.machine_name)
      toast.success(res.log || "Conexao validada.")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha no teste.")
    } finally {
      setTestingName(null)
    }
  }

  const handleToggleStatus = async (destination: Destination) => {
    setTogglingName(destination.machine_name)
    try {
      const res = await api.setDestinationStatus(
        destination.machine_name,
        !destination.is_active
      )
      onDestinationsChange(res.destinations)
      toast.success(res.log)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Erro ao alterar status do ambiente."
      )
    } finally {
      setTogglingName(null)
    }
  }

  if (!isAdmin) {
    return (
      <div className="flex w-full flex-1 flex-col items-start justify-start p-4 sm:p-6 md:p-8">
        <p className="text-sm text-muted-foreground">
          Sem permissao para acessar esta secao.
        </p>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-hidden p-4 sm:p-6 md:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Ambientes</h1>
          <p className="text-sm text-muted-foreground">
            Configure os destinos SSH usados nas operacoes VPN. Cada ambiente
            usa um certificado cadastrado em Certificados.
          </p>
        </div>
        <Button size="sm" onClick={openNew}>
          <Plus />
          Novo ambiente
        </Button>
      </div>

      <Card className="min-h-0 flex-1 overflow-hidden">
        <CardHeader className="shrink-0 pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-sm font-medium">
                Ambientes cadastrados
              </CardTitle>
              <CardDescription className="text-xs">
                {loading
                  ? "Carregando..."
                  : `${destinations.length} ambiente(s)`}
              </CardDescription>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar ambiente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 text-sm"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col overflow-hidden p-0">
          {loading ? (
            <div className="w-full space-y-2 px-6 pb-4">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : destinations.length === 0 ? (
            <div className="flex min-h-48 w-full flex-col items-start gap-3 px-6 py-12 sm:min-h-56">
              <Server className="size-8 text-muted-foreground/40" />
              <p className="w-full text-left text-sm text-muted-foreground">
                Nenhum ambiente cadastrado.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="mt-1"
                onClick={openNew}
              >
                <Plus />
                Adicionar ambiente
              </Button>
            </div>
          ) : filteredDestinations.length === 0 ? (
            <div className="flex min-h-48 w-full flex-col items-start gap-3 px-6 py-12 sm:min-h-56">
              <Server className="size-8 text-muted-foreground/40" />
              <p className="w-full text-left text-sm text-muted-foreground">
                Nenhum ambiente encontrado para a busca.
              </p>
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="w-full min-w-[920px] border-separate border-spacing-0 text-sm">
                <thead>
                  <tr>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      Ambiente
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      SSH
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      OpenVPN
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      Certificado
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      Status
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {filteredDestinations.map((destination) => {
                    const cert =
                      destination.certificate_id == null
                        ? null
                        : certificateById.get(destination.certificate_id)
                    return (
                      <tr
                        key={destination.machine_name}
                        className={`transition-colors last:[&>td]:border-0 hover:bg-muted/30 ${
                          destination.is_active ? "" : "opacity-60"
                        }`}
                      >
                        <td className="border-b px-4 py-3">
                          <div className="flex min-w-0 flex-col">
                            <span className="font-medium">
                              {destination.machine_name}
                            </span>
                            <span className="font-mono text-[11px] text-muted-foreground">
                              {destination.remote_file_template}
                            </span>
                          </div>
                        </td>
                        <td className="border-b px-4 py-3">
                          <div className="flex min-w-0 flex-col">
                            <span className="font-mono text-xs">
                              {destination.ssh_user}@{destination.ssh_host}
                            </span>
                          </div>
                        </td>
                        <td className="border-b px-4 py-3">
                          <div className="flex min-w-0 flex-col gap-1">
                            <Badge variant="secondary" className="w-fit font-mono text-[10px]">
                              {destination.openvpn_mode}
                            </Badge>
                            <span className="font-mono text-[11px] text-muted-foreground">
                              {destination.openvpn_script_path}
                            </span>
                          </div>
                        </td>
                        <td className="border-b px-4 py-3">
                          {cert ? (
                            <div className="flex items-center gap-2">
                              <KeyRound className="size-3.5 text-primary" />
                              <span className="text-xs">{cert.label}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              Sem certificado
                            </span>
                          )}
                        </td>
                        <td className="border-b px-4 py-3">
                          <Badge
                            variant={destination.is_active ? "default" : "outline"}
                            className="w-fit text-[10px]"
                          >
                            {destination.is_active ? "Ativo" : "Desativado"}
                          </Badge>
                        </td>
                        <td className="border-b px-4 py-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="size-7 p-0"
                              >
                                <MoreHorizontal className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => handleTest(destination)}
                                disabled={
                                  testingName === destination.machine_name ||
                                  destination.certificate_id == null
                                }
                              >
                                <Cable className="mr-2 size-4" />
                                Testar conexao
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openEdit(destination)}
                              >
                                <Pencil className="mr-2 size-4" />
                                Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleToggleStatus(destination)}
                                disabled={togglingName === destination.machine_name}
                              >
                                {destination.is_active ? (
                                  <>
                                    <PowerOff className="mr-2 size-4" />
                                    Desativar
                                  </>
                                ) : (
                                  <>
                                    <Power className="mr-2 size-4" />
                                    Ativar
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setDeleteTarget(destination)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="mr-2 size-4" />
                                Remover
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>
              {editing ? "Editar ambiente" : "Novo ambiente"}
            </SheetTitle>
            <SheetDescription>
              {editing
                ? "Atualize o destino usado pelo painel."
                : "Cadastre um destino SSH para o painel."}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="flex flex-1 flex-col">
            <div className="grid gap-4 px-4">
              <div className="grid gap-2">
                <Label htmlFor="destination-name">Nome exibido *</Label>
                <Input
                  id="destination-name"
                  value={form.machine_name}
                  onChange={(e) => updateForm("machine_name", e.target.value)}
                  placeholder="AtualC"
                  disabled={saving}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="destination-host">Host SSH *</Label>
                <Input
                  id="destination-host"
                  value={form.ssh_host}
                  onChange={(e) => updateForm("ssh_host", e.target.value)}
                  placeholder="vpn.cliente-prod.onz.software"
                  disabled={saving}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="destination-user">Usuario SSH *</Label>
                <Input
                  id="destination-user"
                  value={form.ssh_user}
                  onChange={(e) => updateForm("ssh_user", e.target.value)}
                  placeholder="ec2-user"
                  disabled={saving}
                />
              </div>

              <div className="grid gap-2">
                <Label>Certificado SSH *</Label>
                <Select
                  value={
                    selectedCertificateId == null
                      ? undefined
                      : String(selectedCertificateId)
                  }
                  onValueChange={(value) =>
                    updateForm("certificate_id", value)
                  }
                  disabled={saving || certificates.length === 0}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Selecione um certificado cadastrado" />
                  </SelectTrigger>
                  <SelectContent>
                    {certificates.map((cert) => (
                      <SelectItem key={cert.id} value={String(cert.id)}>
                        {cert.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {certificates.length === 0 ? (
                  <p className="text-xs text-amber-600">
                    Cadastre um certificado em Certificados antes de salvar o
                    ambiente.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Chave privada usada no SSH deste ambiente.
                  </p>
                )}
              </div>

              <div className="grid gap-2">
                <Label>Modo OpenVPN *</Label>
                <Select
                  value={form.openvpn_mode}
                  onValueChange={(value) => updateForm("openvpn_mode", value)}
                  disabled={saving}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="modern_cli">modern_cli</SelectItem>
                    <SelectItem value="legacy_menu_v1">
                      legacy_menu_v1
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="destination-script">Script OpenVPN *</Label>
                <Input
                  id="destination-script"
                  value={form.openvpn_script_path}
                  onChange={(e) =>
                    updateForm("openvpn_script_path", e.target.value)
                  }
                  placeholder="/root/openvpn-install.sh"
                  disabled={saving}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="destination-template">Arquivo .ovpn *</Label>
                <Input
                  id="destination-template"
                  value={form.remote_file_template}
                  onChange={(e) =>
                    updateForm("remote_file_template", e.target.value)
                  }
                  placeholder="~/{client_name}.ovpn"
                  disabled={saving}
                />
              </div>
            </div>

            <SheetFooter>
              <Button type="submit" disabled={submitDisabled}>
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover ambiente</AlertDialogTitle>
            <AlertDialogDescription>
              O ambiente "{deleteTarget?.machine_name}" sera removido da lista.
              Os registros historicos permanecem no banco.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="text-destructive-foreground bg-destructive hover:bg-destructive/90"
            >
              {deleting ? "Removendo..." : "Remover"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
